<template>
  <aside :class="['outer', { 'outer--visible': visible }]">
    <div class="shadow" @click="onToggle"></div>
    <div class="inner">
      <div class="logo">
        <div>
          <img :src="themeImage('logo-white.svg')" :alt="`${partnershipName} Logo`" />
        </div>
        <font-awesome-icon class="aside-close" size="2x" icon="times" @click="onToggle" />
      </div>
      <template v-for="(el, index) of visibleElements">
        <template v-if="el.type === ELEMENT_TYPE.LINK">
          <router-link
            @click.native="onClick(el)"
            :to="{ name: el.link ? el.link : '' }"
            :class="{
              'element-link': true,
              'element-link-active': $route.path.startsWith($router.resolve({ name: el.link }).route.path),
              'element-link--disabled': isDisabledForOnboarding(el),
              [el.class || '']: true,
            }"
            :event="isDisabledForOnboarding(el) ? '' : 'click'"
            :key="index"
            :id="el.link"
          >
            <font-awesome-icon v-if="el.icon" :icon="el.icon" class="mr-3" fixed-width />
            <span>{{ el.i18nKey ? $t(el.i18nKey) : el.text }}</span>
            <span
              v-if="el.link === 'sme-dashboard' && outstandingTodosCount > 0 && portalUpdatesJan2026Enabled"
              class="nav-badge"
              >{{ outstandingTodosCount }}</span
            >
          </router-link>
        </template>
        <template v-if="el.type === ELEMENT_TYPE.EXTERNAL_LINK">
          <div
            :class="{
              'element-link': true,
              'element-link--external': true,
              'element-link--disabled': isOnboardingOnly,
            }"
            :key="index"
            role="link"
            @click="isOnboardingOnly ? null : openExternal(el.href)"
          >
            <font-awesome-icon v-if="el.icon" :icon="el.icon" class="mr-3" fixed-width />
            <span>{{ el.i18nKey ? $t(el.i18nKey) : el.text }}</span>
          </div>
        </template>
        <template v-if="el.type === ELEMENT_TYPE.DIVIDER">
          <hr class="element-divider" :key="index" />
        </template>
      </template>
      <div class="bottom">
        <img v-if="themeId" src="@/assets/powered-by-wagestream-white.svg" alt="Powered by Stream" class="powered-by" />
        <ReferralsAdvertisment v-else-if="shouldShowReferralsAdvertisment && !isDisablePortalReferrals" />
      </div>
    </div>
  </aside>
</template>

<script>
import { themeImage } from '@/Theme';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useLaunchState from '@/state/composables/useLaunchState';
import State from '@/state/State';
import { getPartnershipData } from '@/utils/partnership';
import ReferralsAdvertisment from '@/components/ReferralsAdvertisment.vue';
import { isScopedRecognitionClaims } from '@/utils/scopedAdminGate';
import { isConfinedToOnboarding } from '@/guards.js';
import { computeVisibleElements } from '@/components/appAsideElements';

const ELEMENT_TYPE = {
  DIVIDER: 'DIVIDER',
  SPACER: 'SPACER',
  LINK: 'LINK',
  EXTERNAL_LINK: 'EXTERNAL_LINK',
};

export default {
  name: 'AppAside',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    ReferralsAdvertisment,
  },
  data() {
    const { name: partnershipName, themeId } = getPartnershipData();

    const showOffCycleStandalone = this.offCyclePaymentsVersion === 'v2-standalone';

    const state = State.state;
    const defaultPages = [
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'rocket-launch'],
        link: 'onboarding',
        text: 'Onboarding',
        i18nKey: 'nav.onboarding',
        hide: () => !isConfinedToOnboarding(),
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fas', 'analytics'],
        link: 'sme-dashboard',
        text: 'Dashboard',
        i18nKey: 'nav.dashboard',
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'chart-pie'],
        link: 'analytics-homepage',
        text: this.portalUpdatesJan2026Enabled ? 'Impact & Engagement' : 'Analytics',
        i18nKey: this.portalUpdatesJan2026Enabled ? 'nav.impactEngagement' : 'nav.analytics',
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'info-circle'],
        link: 'help-center',
        text: 'Help Center',
        i18nKey: 'nav.helpCenter',
        hide: !state.company.properties.portal?.can_view_help_center,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fal', 'question-circle'],
        link: 'help',
        text: 'Help',
        i18nKey: 'nav.help',
        hide: !state.company.properties.portal?.can_access_intercom,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'donate'],
        link: 'funding-position',
        text: 'Funding Position',
        i18nKey: 'nav.fundingPosition',
        hide: !state.claims.a || this.isFundingPositionPageDisabled,
      },
      {
        type: ELEMENT_TYPE.DIVIDER,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'users'],
        link: 'employees',
        text: 'Employees',
        i18nKey: 'nav.employees',
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'gift'],
        link: 'recognition',
        text: 'Recognition',
        i18nKey: 'nav.recognition',
        hide: this.recognitionVersion === 'off',
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'calendar-alt'],
        link: 'shifts',
        text: 'Shifts',
        i18nKey: 'nav.shifts',
        hide: this.hideShiftsNav,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'user-clock'],
        link: 'absences',
        text: 'Absences',
        i18nKey: 'nav.absences',
        hide: !state.claims.a || !this.canViewAbsences,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'briefcase'],
        link: 'jobs',
        text: 'Jobs',
        i18nKey: 'nav.jobs',
        hide: !state.claims.a || (!this.canViewJobs && !this.canAddJobs),
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'money-check-alt'],
        link: 'sme-pay-day',
        text: 'Pay',
        i18nKey: 'nav.pay',
        hide: !state.claims.a,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'file-invoice-dollar'],
        link: 'last-day-pay',
        text: 'Last Day Pay Report',
        i18nKey: 'nav.lastDayPay',
        hide: this.hideSalaryView || (this.offCyclePaymentsVersion === 'off' ? !this.isEnableLastDayPay : true),
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'exchange'],
        link: 'streams',
        text: 'Transferred Wages',
        i18nKey: 'nav.transferredWages',
        hide: !(this.isEnableStreamedWagesPage || this.isEnableShowPayschedulesOnStreamedWagesPage),
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['far', 'credit-card'],
        link: 'employee-payments',
        text: 'Employee Payments',
        i18nKey: 'nav.employeePayments',
        hide: !this.isEnableEmployeePaymentsPage || this.hideSalaryView,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'file-invoice-dollar'],
        link: 'salary-settlement',
        text: 'Salary Settlement',
        i18nKey: 'nav.salarySettlement',
        hide: !this.canViewSalarySettlementPage || this.hideSalaryView,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'chart-line'],
        link: 'reporting',
        text: 'Reporting',
        hide: !this.isEnableReportingPage,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fas', 'rotate'],
        link: 'one-time-payments',
        text: 'Off-cycle Payments',
        i18nKey: 'nav.offCyclePayments',
        hide: this.offCyclePaymentsVersion !== 'v2',
      },
      {
        type: ELEMENT_TYPE.DIVIDER,
      },
      {
        type: ELEMENT_TYPE.EXTERNAL_LINK,
        icon: ['fad', 'bullhorn'],
        href: this.isNhsCompany
          ? 'https://v1.pallery.com/stream-for-nhs-99f1a'
          : 'https://v1.pallery.com/stream-for-communications-hub-5c1e4',
        text: 'Comms Hub',
        i18nKey: 'nav.commsHub',
        hide: !this.isUkCompany,
      },
      {
        type: ELEMENT_TYPE.DIVIDER,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'university'],
        link: 'sme-funds',
        text: 'Funds',
        i18nKey: 'nav.funds',
        hide: () => !this.isSelfFunded,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'users-cog'],
        link: 'admins',
        text: 'Admins',
        i18nKey: 'nav.admins',
        hide: !state.claims.a || !state.canAdminAdmins,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'sitemap'],
        link: 'divisions',
        text: 'Divisions',
        i18nKey: 'nav.divisions',
        hide: this.isUsCompany || !state.claims.a || !state.canAdminAdmins,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'chart-network'],
        link: 'sme-integrations',
        text: 'Integrations',
        i18nKey: 'nav.integrations',
        hide: !state.claims.a || !state.canAdminAdmins,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'receipt'],
        link: this.showDeductionsFiles ? 'one-time-payments-invoices' : 'sme-subscription',
        text: 'Invoices',
        i18nKey: 'nav.invoices',
        hide:
          (!this.isUkCompany && !this.showDeductionsFiles) || this.hideInvoicePage
            ? true
            : !state.claims.a || !state.canAdminAdmins,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'cogs'],
        link: 'company-settings',
        text: 'Settings',
        i18nKey: 'nav.settings',
        hide: !state.claims.a,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['fad', 'upload'],
        link: 'secure-file-transfer',
        text: 'Secure File Transfer',
        i18nKey: 'nav.secureFileTransfer',
        hide: !state.claims.a || !state?.company?.properties?.portal?.show_file_upload,
      },
      {
        type: ELEMENT_TYPE.LINK,
        icon: ['far', 'clock'],
        link: 'recent-file-uploads',
        text: 'Recent Uploads',
        i18nKey: 'nav.recentUploads',
        hide: !state.claims.a || !state?.company?.properties?.portal?.show_recent_file_uploads,
      },
    ];

    // Recognition-only admins get a single-item menu; the router confinement
    // gate (scopedAdminGate) backs this up for direct URL entry.
    if (isScopedRecognitionClaims(state.claims)) {
      return {
        ELEMENT_TYPE,
        partnershipName,
        themeId,
        elements: [
          {
            type: ELEMENT_TYPE.LINK,
            icon: ['fad', 'gift'],
            link: 'recognition',
            text: 'Recognition',
            i18nKey: 'nav.recognition',
            hide: this.recognitionVersion === 'off',
          },
        ],
      };
    }

    if (showOffCycleStandalone) {
      const pages = [
        {
          type: ELEMENT_TYPE.LINK,
          icon: ['fad', 'chart-pie'],
          link: 'analytics-homepage',
          text: this.portalUpdatesJan2026Enabled ? 'Impact & Engagement' : 'Analytics',
          i18nKey: this.portalUpdatesJan2026Enabled ? 'nav.impactEngagement' : 'nav.analytics',
        },
        {
          type: ELEMENT_TYPE.LINK,
          icon: ['fas', 'rotate'],
          link: 'one-time-payments',
          text: 'Off-cycle Payments',
          i18nKey: 'nav.offCyclePayments',
        },
      ];
      return {
        ELEMENT_TYPE,
        partnershipName,
        themeId,
        elements: pages,
      };
    }

    const pages = defaultPages.concat({
      type: ELEMENT_TYPE.SPACER,
    });

    return {
      ELEMENT_TYPE,
      partnershipName,
      themeId,
      elements: pages,
    };
  },
  setup() {
    const { outstandingTodosCount, getLaunchState } = useLaunchState();
    getLaunchState(true);

    const {
      isEnableStreamedWagesPage,
      isEnableShowPayschedulesOnStreamedWagesPage,
      isEnableEmployeePaymentsPage,
      isFundingPositionPageDisabled,
      canIntegrate,
      isSelfFunded,
      isSubscription,
      canViewIntegrationHistory,
      canViewAbsences,
      canViewJobs,
      canAddJobs,
      shouldShowReferralsAdvertisment,
      isEnableLastDayPay,
      isDisablePortalReferrals,
      offCyclePaymentsVersion,
      recognitionVersion,
      isEnableSmeLaunchV2,
      isUkCompany,
      isNhsCompany,
      hideInvoicePage,
      showDeductionsFiles,
      canViewSalarySettlementPage,
      portalUpdatesJan2026Enabled,
      hideSalaryView,
      isEnableReportingPage,
      isUsCompany,
      hideShiftsNav,
    } = useFeatureFlags();

    return {
      outstandingTodosCount,
      isEnableStreamedWagesPage,
      isEnableShowPayschedulesOnStreamedWagesPage,
      isEnableEmployeePaymentsPage,
      isFundingPositionPageDisabled,
      canIntegrate,
      isSelfFunded,
      isSubscription,
      canViewIntegrationHistory,
      canViewAbsences,
      canViewJobs,
      canAddJobs,
      shouldShowReferralsAdvertisment,
      isEnableLastDayPay,
      isDisablePortalReferrals,
      offCyclePaymentsVersion,
      recognitionVersion,
      isEnableSmeLaunchV2,
      isUkCompany,
      isNhsCompany,
      hideInvoicePage,
      showDeductionsFiles,
      canViewSalarySettlementPage,
      portalUpdatesJan2026Enabled,
      hideSalaryView,
      isEnableReportingPage,
      isUsCompany,
      hideShiftsNav,
    };
  },
  computed: {
    // Users confined to onboarding see the full sidebar but every nav item
    // except /onboarding is visually disabled and non-clickable. The router gate
    // enforces the restriction; this just makes the disabled state visible
    // instead of hiding the chrome.
    isOnboardingOnly() {
      return isConfinedToOnboarding();
    },
    // The nav is a flat array of links and DIVIDER separators. After hidden
    // items are dropped, dividers can become redundant (leading, trailing, or
    // adjacent to another divider) and would render as stray / doubled lines
    // (e.g. US companies hide the "Comms Hub" link sitting between two
    // dividers). Collapse those so each divider always separates real items.
    visibleElements() {
      return computeVisibleElements(this.elements, el => this.shouldHide(el));
    },
  },
  methods: {
    themeImage,
    isDisabledForOnboarding(el) {
      return this.isOnboardingOnly && el.link !== 'onboarding';
    },
    shouldHide(el) {
      if (!el.hide) {
        if (this.isEnableSmeLaunchV2) {
          if (!['sme-dashboard', 'analytics-homepage', 'sme-subscription'].includes(el.link)) {
            return true;
          }
        }
        return false;
      }
      if (typeof el.hide === 'boolean') {
        return el.hide;
      } else {
        return el.hide();
      }
    },
    onClick(el) {
      if (el.click) {
        el.click();
      }
    },
    openExternal(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onToggle() {
      this.$emit('toggle');
    },
  },
};
</script>

<style scoped lang="scss">
.outer {
  position: sticky;
  background: var(--palette-color-brand-primary) !important;
  color: var(--palette-color-base-white);
  min-width: 250px;
  max-width: 250px;
  height: 100%;
}

.inner {
  display: flex;
  position: sticky;
  box-sizing: content-box;
  flex-direction: column;
  background: var(--palette-color-brand-primary);
  overflow: auto;
  top: 0;
  max-height: 100vh;
  height: 100%;
}

.logo {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 5.25rem;
  justify-content: center;
  width: 100%;
}

.logo img {
  display: block;
  height: 2.5rem;
  width: auto;
}

.logo .aside-close {
  display: none;
  position: absolute;
  cursor: pointer;
  color: var(--palette-color-base-white);
  right: 16px;
  top: 19px;
}

.element-link {
  display: flex;
  position: relative;
  padding: 0.8rem 1.5rem;
  color: var(--palette-color-base-white);
  font-size: 14px;

  @media (max-height: 950px) {
    font-size: 14px;
    padding: 0.6rem 1.5rem;
  }

  @media (max-height: 825px) {
    font-size: 13px;
    padding: 0.5rem 1.5rem;
  }
}

.element-link--external {
  cursor: pointer;
}

.element-link-active,
.element-link:not(.element-link--disabled):hover {
  background: var(--palette-color-brand-primary-lighten-20);
}

.element-link--disabled,
.element-link--disabled:hover {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
  background: transparent !important;
}

.element-link--disabled:before {
  display: none !important;
}

.element-link-active:before {
  display: block;
  position: absolute;
  background: var(--palette-color-core-5);
  content: '';
  height: 100%;
  width: 4px;
  left: 0;
  top: 0;
}

.element-link svg {
  font-size: 17.5px;
}

.nav-badge {
  background: var(--palette-color-core-5);
  color: var(--palette-color-base-white);
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  padding: 0 5px;
  margin-left: auto;
}

.element-divider {
  border-top: 0;
  margin: 15px;
  border: 1px solid var(--palette-color-base-white);
  opacity: 0.1;
}

.element-spacer {
  height: 100%;
}

.shadow {
  display: none;
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  cursor: pointer;
  top: 0;
  left: 250px;
  height: 200vh;
  width: 100vh;
}

.powered-by {
  margin: 1.5rem auto;
  max-width: 6.25rem;
}

@media (max-width: 768px) {
  .outer {
    animation-fill-mode: forwards;
    transform: translateX(0);
    transition: opacity 0.25s, transform 0.25s;
    position: fixed;
    z-index: 1001;
    opacity: 1;
  }

  .outer:not(.outer--visible) {
    pointer-events: none;
    transform: translateX(-100%);
    opacity: 0;
  }

  .shadow,
  .aside-close {
    display: block !important;
  }

  .element-link {
    font-weight: 300;
    font-size: 16px;
  }
}

@media (min-width: 768px) {
  .outer:not(.outer--visible) {
    display: none;
  }
}

.bottom {
  display: flex;
  height: 100%;
  align-items: flex-end;
  justify-content: center;
  margin-top: 2rem;
}
</style>
